import i18n from '../../i18n/config';
import { useTranslation } from 'react-i18next';
// frontend/src/components/settings/CompanyNetworksTab.jsx
// Tab "Redes" en Empresa → Configuración. Registro de redes WiFi de la empresa.
import React, { useEffect, useMemo, useState } from 'react';
import {
    Accordion, AccordionDetails, AccordionSummary,
    Alert, Autocomplete, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog,
    DialogActions, DialogContent, DialogTitle, Divider, FormControl, FormControlLabel,
    Grid, IconButton, InputAdornment, InputLabel, MenuItem, Paper, Select, Stack, Switch,
    TextField, Tooltip, Typography
} from '@mui/material';
import {
    Add as AddIcon, Wifi as WifiIcon, Visibility as ShowIcon, VisibilityOff as HideIcon,
    Edit as EditIcon, Delete as DeleteIcon, Email as EmailIcon, CheckCircle as CheckIcon,
    Router as RouterIcon, Refresh as RefreshIcon, Person as PersonIcon,
    ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { api } from '../../services/api';
import { useNotification } from '../common/GlobalNotifications';
import PhoneInput from '../common/PhoneInput';

const userLabel = (u) => {
    if (!u) return '';
    const full = `${u.name || ''} ${u.last_name || ''}`.trim() || u.full_name || u.email;
    return `${full} · ${u.email}`;
};

const blankForm = () => ({
    ssid: '', password: '', notes: '', active: true,
    requires_mac_registration: false,
    mac_responsible_type: 'aps_user',
    mac_responsible_user_id: '',
    mac_responsible_name: '', mac_responsible_last_name: '',
    mac_responsible_phone: '', mac_responsible_email: '',
});

const NetworkDialog = ({ open, onClose, onSaved, users, initial }) => {
  const { t } = useTranslation();
    const { showError, showSuccess } = useNotification();
    const [form, setForm] = useState(blankForm());
    const [showPass, setShowPass] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        if (initial) {
            setForm({
                ssid: initial.ssid || '',
                password: initial.password || '',
                notes: initial.notes || '',
                active: initial.active !== false,
                requires_mac_registration: !!initial.requires_mac_registration,
                mac_responsible_type: initial.mac_responsible_type || 'aps_user',
                mac_responsible_user_id: initial.mac_responsible_user_id || '',
                mac_responsible_name: initial.mac_responsible_name || '',
                mac_responsible_last_name: initial.mac_responsible_last_name || '',
                mac_responsible_phone: initial.mac_responsible_phone || '',
                mac_responsible_email: initial.mac_responsible_email || '',
            });
        } else {
            setForm(blankForm());
        }
    }, [open, initial]);

    const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

    const save = async () => {
        if (!form.ssid.trim()) { showError(t('hardcoded.components_settings_companynetworkstab.el_nombre_de_la_red')); return; }
        if (form.requires_mac_registration) {
            if (form.mac_responsible_type === 'aps_user' && !form.mac_responsible_user_id) {
                showError(t('hardcoded.components_settings_companynetworkstab.selecciona_el_usuario_responsable_del')); return;
            }
            if (form.mac_responsible_type === 'external') {
                if (!form.mac_responsible_name.trim()) { showError(t('hardcoded.components_settings_companynetworkstab.nombre_del_responsable_requerido')); return; }
                if (!form.mac_responsible_email.trim()) { showError(t('hardcoded.components_settings_companynetworkstab.correo_del_responsable_requerido_para')); return; }
            }
        }
        setSaving(true);
        try {
            const payload = { ...form };
            if (!payload.mac_responsible_user_id) delete payload.mac_responsible_user_id;
            if (initial?.id) {
                await api.companyNetworks.update(initial.id, payload);
                showSuccess(t('hardcoded.components_settings_companynetworkstab.red_actualizada'));
            } else {
                await api.companyNetworks.create(payload);
                showSuccess(form.requires_mac_registration ? t('hardcoded.components_settings_companynetworkstab.red_creada_correo_enviado_al') : t('hardcoded.components_settings_companynetworkstab.red_creada'));
            }
            onSaved?.();
            onClose();
        } catch (e) {
            showError(e?.response?.data?.detail || t('hardcoded.components_settings_companynetworkstab.no_se_pudo_guardar_la'));
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{initial ? t('hardcoded.components_settings_companynetworkstab.editar_red') : t('hardcoded.components_settings_companynetworkstab.nueva_red_wifi')}</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <TextField label={t('hardcoded.components_settings_companynetworkstab.nombre_de_la_red_ssid')} value={form.ssid} onChange={set('ssid')} fullWidth autoFocus
                        InputProps={{ startAdornment: <InputAdornment position="start"><WifiIcon fontSize="small" /></InputAdornment> }} />
                    <TextField
                        label={t('createUser.sections.password')} value={form.password} onChange={set('password')} fullWidth
                        type={showPass ? 'text' : 'password'}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setShowPass((s) => !s)}>
                                        {showPass ? <HideIcon fontSize="small" /> : <ShowIcon fontSize="small" />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <TextField label={t('hardcoded.components_settings_companynetworkstab.notas_opcional')} value={form.notes} onChange={set('notes')} fullWidth multiline rows={2} />

                    <Divider />

                    <FormControlLabel
                        control={<Switch checked={form.requires_mac_registration}
                            onChange={(e) => setForm((p) => ({ ...p, requires_mac_registration: e.target.checked }))} />}
                        label={
                            <Stack direction="row" spacing={1} alignItems="center">
                                <RouterIcon fontSize="small" color={form.requires_mac_registration ? 'warning' : 'disabled'} />
                                <span>{t('hardcoded.components_settings_companynetworkstab.requiere_registrar_el_mac_del')}</span>
                            </Stack>
                        }
                    />

                    {form.requires_mac_registration && (
                        <Box sx={{ pl: 1, borderLeft: '3px solid', borderColor: 'warning.main' }}>
                            <Stack spacing={2} sx={{ pl: 1.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                    {t('hardcoded.components_settings_companynetworkstab.quien_registrara_los_mac_en')}</Typography>
                                <FormControl size="small" fullWidth>
                                    <InputLabel>{i18n.t('hardcoded.components_settings_companynetworkstab.responsable')}</InputLabel>
                                    <Select label={t('hardcoded.components_settings_companynetworkstab.responsable')} value={form.mac_responsible_type}
                                        onChange={set('mac_responsible_type')}>
                                        <MenuItem value="aps_user">{t('hardcoded.components_settings_companynetworkstab.un_usuario_de_aps')}</MenuItem>
                                        <MenuItem value="external">{t('components_settings_companynetworkstab.external_person')}</MenuItem>
                                    </Select>
                                </FormControl>

                                {form.mac_responsible_type === 'aps_user' ? (
                                    <Autocomplete
                                        size="small"
                                        options={users}
                                        getOptionLabel={userLabel}
                                        value={users.find((u) => u.id === form.mac_responsible_user_id) || null}
                                        onChange={(_, val) => setForm((p) => ({ ...p, mac_responsible_user_id: val?.id || '' }))}
                                        renderInput={(params) => <TextField {...params} label={t('hardcoded.components_settings_companynetworkstab.usuario_responsable')} placeholder={t('hardcoded.components_settings_companynetworkstab.buscar_usuario')} />}
                                        isOptionEqualToValue={(o, v) => o.id === v.id}
                                    />
                                ) : (
                                    <Grid container spacing={1.5}>
                                        <Grid item xs={6}>
                                            <TextField size="small" fullWidth label={t('components_settings_companynetworkstab.field_name')} value={form.mac_responsible_name} onChange={set('mac_responsible_name')} />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField size="small" fullWidth label={t('createUser.form.lastName')} value={form.mac_responsible_last_name} onChange={set('mac_responsible_last_name')} />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <PhoneInput
                                                size="small"
                                                label={t('hardcoded.components_settings_companynetworkstab.telefono_opcional')}
                                                value={form.mac_responsible_phone}
                                                onChange={(value) => setForm((p) => ({ ...p, mac_responsible_phone: value }))}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField size="small" fullWidth label={t('hardcoded.components_settings_companynetworkstab.correo')} value={form.mac_responsible_email} onChange={set('mac_responsible_email')}
                                                InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon fontSize="small" /></InputAdornment> }} />
                                        </Grid>
                                    </Grid>
                                )}
                            </Stack>
                        </Box>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common.cancel')}</Button>
                <Button variant="contained" onClick={save} disabled={saving} startIcon={saving ? <CircularProgress size={14} /> : <AddIcon />}>
                    {initial ? t('common.save') : t('hardcoded.components_settings_companynetworkstab.crear_red')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const STATUS_LABELS = {
    registered: { label: i18n.t('hardcoded.components_settings_companynetworkstab.registrado'), color: 'success' },
    pending: { label: i18n.t('hardcoded.components_admin_ordermanagement.pendiente'), color: 'warning' },
    no_data: { label: 'Sin datos', color: 'default' },
    missing: { label: i18n.t('hardcoded.components_settings_companynetworkstab.falta_registrar'), color: 'error' },
};

const CompanyNetworksTab = () => {
  const { t } = useTranslation();
    const { showError, showSuccess } = useNotification();
    const [networks, setNetworks] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialog, setDialog] = useState({ open: false, initial: null });
    const [revealPass, setRevealPass] = useState({});

    // Overview de receptores (registrados + faltantes) que vive dentro de Redes.
    const [receivers, setReceivers] = useState({ items: [], summary: {} });
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchRx, setSearchRx] = useState('');
    // Diálogo de receptor (registrar o editar). id null → registrar; con id → editar.
    const [rxDialog, setRxDialog] = useState({ open: false, id: null, mac: '', vehicleId: '', networkId: '' });
    const [registering, setRegistering] = useState(false);

    const loadReceivers = async () => {
        try {
            const r = await api.companyNetworks.receiversOverview();
            setReceivers(r.data || { items: [], summary: {} });
        } catch (e) {
            // No bloquear el tab si falla; sólo notificar.
            showError(t('hardcoded.components_settings_companynetworkstab.no_se_pudo_cargar_el'));
        }
    };

    const load = async () => {
        setLoading(true);
        try {
            const [rNet, rUsers] = await Promise.all([
                api.companyNetworks.list(),
                api.users.getAll().catch(() => ({ data: [] })),
            ]);
            setNetworks(rNet.data || []);
            setUsers(rUsers.data || []);
            await loadReceivers();
        } catch (e) {
            showError(t('hardcoded.components_settings_companynetworkstab.no_se_pudieron_cargar_las'));
        } finally { setLoading(false); }
    };

    const filteredReceivers = useMemo(() => {
        const items = receivers?.items || [];
        const q = (searchRx || '').toLowerCase().trim();
        return items.filter((r) => {
            if (statusFilter !== 'all' && r.status !== statusFilter) return false;
            if (!q) return true;
            const hay = `${r.mac_address || ''} ${r.vehicle_name || ''} ${r.dynamo_receiver_id || ''} ${r.network_ssid || ''}`.toLowerCase();
            return hay.includes(q);
        });
    }, [receivers, statusFilter, searchRx]);

    const closeRxDialog = () => setRxDialog({ open: false, id: null, mac: '', vehicleId: '', networkId: '' });

    const handleSaveReceiver = async () => {
        const mac = (rxDialog.mac || '').toUpperCase().replace(/[:\-\s]/g, '');
        if (mac.length !== 12) { showError(t('hardcoded.components_settings_companynetworkstab.la_mac_debe_tener_12')); return; }
        setRegistering(true);
        try {
            if (rxDialog.id) {
                await api.companyNetworks.updateReceiver(rxDialog.id, {
                    mac_address: mac,
                    vehicle_id: rxDialog.vehicleId || null,
                    network_id: rxDialog.networkId || null,
                });
                showSuccess(t('hardcoded.components_settings_companynetworkstab.receptor_actualizado'));
            } else {
                await api.companyNetworks.registerReceiver({
                    mac_address: mac,
                    vehicle_id: rxDialog.vehicleId || null,
                    network_id: rxDialog.networkId || null,
                });
                showSuccess(t('hardcoded.components_settings_companynetworkstab.receptor_registrado_se_notifico_a'));
            }
            closeRxDialog();
            loadReceivers();
        } catch (e) {
            showError(e?.response?.data?.detail || t('hardcoded.components_settings_companynetworkstab.no_se_pudo_guardar_el'));
        } finally { setRegistering(false); }
    };

    // Receptores agrupados por red (incluye una agrupación "Sin red asignada").
    const receiversByNetwork = useMemo(() => {
        const byId = new Map();
        let noNet = [];
        for (const r of filteredReceivers) {
            if (r.network_id) {
                if (!byId.has(r.network_id)) byId.set(r.network_id, []);
                byId.get(r.network_id).push(r);
            } else {
                noNet.push(r);
            }
        }
        return { byId, noNet };
    }, [filteredReceivers]);

    useEffect(() => { load(); }, []);

    const toggleRegistered = async (net) => {
        try {
            await api.companyNetworks.markRegistered(net.id, !net.mac_registered);
            showSuccess(net.mac_registered ? t('hardcoded.components_settings_companynetworkstab.red_marcada_como_pendiente') : t('hardcoded.components_settings_companynetworkstab.red_marcada_como_registrada'));
            load();
        } catch (e) { showError(t('hardcoded.components_admin_ordermanagement.error_al_actualizar_estado')); }
    };

    const resendEmail = async (net) => {
        try {
            await api.companyNetworks.resendEmail(net.id);
            showSuccess(t('hardcoded.components_settings_companynetworkstab.correo_reenviado_al_responsable'));
            load();
        } catch (e) { showError(e?.response?.data?.detail || t('hardcoded.components_settings_companynetworkstab.no_se_pudo_reenviar_el')); }
    };

    const remove = async (net) => {
        if (!window.confirm(t('hardcoded.components_settings_companynetworkstab.eliminar_la_red_ssid', { ssid: net.ssid }))) return;
        try {
            await api.companyNetworks.delete(net.id);
            showSuccess(t('hardcoded.components_settings_companynetworkstab.red_eliminada'));
            load();
        } catch (e) { showError(t('hardcoded.components_settings_companynetworkstab.error_al_eliminar')); }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

    // Tabla compacta de receptores reutilizable (dentro de cada acordeón de red).
    const renderReceiverTable = (list) => (
        <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <Box component="thead" sx={{ '& th': { textAlign: 'left', borderBottom: '1px solid', borderColor: 'divider', py: 0.75, px: 1, fontWeight: 700 } }}>
                    <tr>
                        <th>{i18n.t('gps.table.status')}</th>
                        <th>MAC</th>
                        <th>{t('vehicles.table.vehicle')}</th>
                        <th>{t('components_settings_companynetworkstab.online_status')}</th>
                        <th>{t('hardcoded.components_schematics_vehicleticketsdrawer.ultimo_reporte')}</th>
                        <th align="right"></th>
                    </tr>
                </Box>
                <Box component="tbody" sx={{ '& td': { borderBottom: '1px dashed', borderColor: 'divider', py: 0.75, px: 1, verticalAlign: 'middle' } }}>
                    {list.length === 0 ? (
                        <tr>
                            <td colSpan={6} style={{ textAlign: 'center', padding: '16px' }}>
                                <Typography variant="body2" color="text.secondary">{t('hardcoded.components_settings_companynetworkstab.sin_receptores')}</Typography>
                            </td>
                        </tr>
                    ) : list.map((r, idx) => {
                        const st = STATUS_LABELS[r.status] || STATUS_LABELS.no_data;
                        return (
                            <tr key={`${r.id || 'missing'}-${idx}`}>
                                <td><Chip size="small" color={st.color} label={st.label} sx={{ height: 22 }} /></td>
                                <td style={{ fontFamily: 'monospace' }}>{r.mac_address || <em style={{ color: '#c62828' }}>{i18n.t('hardcoded.components_settings_companynetworkstab.falta_mac')}</em>}</td>
                                <td>{r.vehicle_name || (r.status === 'pending' ? <em>{t('hardcoded.components_settings_companynetworkstab.sin_vehiculo')}</em> : '—')}</td>
                                <td>{r.online ? <Chip size="small" color="success" label={t('components_settings_companynetworkstab.chip_online')} sx={{ height: 20 }} /> : <Chip size="small" label={t('components_settings_companynetworkstab.chip_offline')} sx={{ height: 20 }} />}</td>
                                <td>{r.last_update_at ? new Date(r.last_update_at).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</td>
                                <td align="right">
                                    {r.id ? (
                                        <Tooltip title={t('hardcoded.components_settings_companynetworkstab.editar_mac_red_vehiculo')}>
                                            <IconButton size="small" onClick={() => setRxDialog({ open: true, id: r.id, mac: r.mac_address || '', vehicleId: r.vehicle_id || '', networkId: r.network_id || '' })}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    ) : (
                                        <Tooltip title={t('hardcoded.components_settings_companynetworkstab.agregar_la_mac_de_este')}>
                                            <Button size="small" startIcon={<AddIcon />} onClick={() => setRxDialog({ open: true, id: null, mac: '', vehicleId: '', networkId: r.network_id || '' })}>
                                                Agregar MAC
                                            </Button>
                                        </Tooltip>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </Box>
            </Box>
        </Box>
    );

    return (
        <Box>
            {/* ============= 1) Redes WiFi (PRIMERO) ============= */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <WifiIcon color="primary" />
                    <Typography variant="subtitle1" fontWeight={700}>{i18n.t('hardcoded.components_settings_companynetworkstab.redes_wifi')}</Typography>
                </Stack>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialog({ open: true, initial: null })}>
                    {i18n.t('hardcoded.components_settings_companynetworkstab.nueva_red')}</Button>
            </Stack>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                {networks.map((net) => {
                    const netReceivers = receiversByNetwork.byId.get(net.id) || [];
                    return (
                    <Grid item xs={12} md={6} key={net.id}>
                        <Card variant="outlined">
                            <CardContent>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                    <WifiIcon color={net.active ? 'primary' : 'disabled'} />
                                    <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>{net.ssid}</Typography>
                                    <Chip size="small" variant="outlined" icon={<RouterIcon />} label={t('hardcoded.components_settings_companynetworkstab.length_receptornetreceiverslen', { length: netReceivers.length, netReceiverslen: netReceivers.length === 1 ? '' : 'es' })} />
                                    {net.requires_mac_registration && (
                                        net.mac_registered
                                            ? <Chip size="small" color="success" icon={<CheckIcon />} label={t('hardcoded.components_settings_companynetworkstab.registrada')} />
                                            : <Chip size="small" color="warning" icon={<RouterIcon />} label={t('hardcoded.components_settings_companynetworkstab.mac_pendiente')} />
                                    )}
                                </Stack>

                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                    <Typography variant="body2" color="text.secondary">{t('hardcoded.components_settings_companynetworkstab.contrasena')}</Typography>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                        {net.password ? (revealPass[net.id] ? net.password : '••••••••') : '—'}
                                    </Typography>
                                    {net.password && (
                                        <IconButton size="small" onClick={() => setRevealPass((p) => ({ ...p, [net.id]: !p[net.id] }))}>
                                            {revealPass[net.id] ? <HideIcon fontSize="small" /> : <ShowIcon fontSize="small" />}
                                        </IconButton>
                                    )}
                                </Stack>

                                {net.requires_mac_registration && (
                                    <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 1.5, mb: 1 }}>
                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                            <PersonIcon fontSize="small" color="action" />
                                            <Typography variant="caption" fontWeight={700}>{t('hardcoded.components_settings_companynetworkstab.responsable_de_registro_mac')}</Typography>
                                        </Stack>
                                        <Typography variant="body2">{net.resolved_responsible_name || '—'}</Typography>
                                        <Typography variant="caption" color="text.secondary">{net.resolved_responsible_email || t('hardcoded.components_settings_companynetworkstab.sin_correo')}</Typography>
                                        {net.mac_responsible_phone && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{i18n.t('hardcoded.components_admin_ordermanagement.tel')}{net.mac_responsible_phone}</Typography>
                                        )}
                                        {net.mac_email_sent_at && (
                                            <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 0.5 }}>
                                                {t('hardcoded.components_settings_companynetworkstab.correo_enviado_el')}{new Date(net.mac_email_sent_at).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}
                                            </Typography>
                                        )}
                                    </Box>
                                )}

                                {net.notes && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{net.notes}</Typography>}

                                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                                    {net.requires_mac_registration && (
                                        <Button size="small" variant={net.mac_registered ? 'outlined' : 'contained'} color={net.mac_registered ? 'inherit' : 'success'}
                                            startIcon={<CheckIcon />} onClick={() => toggleRegistered(net)}>
                                            {net.mac_registered ? t('hardcoded.components_settings_companynetworkstab.marcar_pendiente') : t('hardcoded.components_settings_companynetworkstab.marcar_registrada')}
                                        </Button>
                                    )}
                                    {net.requires_mac_registration && net.resolved_responsible_email && (
                                        <Tooltip title={t('hardcoded.components_settings_companynetworkstab.reenviar_correo_con_los_mac')}>
                                            <Button size="small" startIcon={<EmailIcon />} onClick={() => resendEmail(net)}>{i18n.t('hardcoded.components_settings_companynetworkstab.reenviar')}</Button>
                                        </Tooltip>
                                    )}
                                    <Button size="small" startIcon={<EditIcon />} onClick={() => setDialog({ open: true, initial: net })}>{i18n.t('common.edit')}</Button>
                                    <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => remove(net)}>{t('audit.actions.DELETE')}</Button>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                    );
                })}
                {!networks.length && (
                    <Grid item xs={12}>
                        <Alert severity="info">{t('hardcoded.components_settings_companynetworkstab.aun_no_hay_redes_registradas')}</Alert>
                    </Grid>
                )}
            </Grid>

            {/* ============= 2) Receptores agrupados/colapsados por red ============= */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" sx={{ mb: 1.5 }} spacing={1}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <RouterIcon color="primary" />
                        <Typography variant="subtitle1" fontWeight={700}>{t('hardcoded.components_settings_companynetworkstab.receptores_por_red')}</Typography>
                        <Tooltip title={t('hardcoded.components_operators_operatorvehiclespanel.recargar')}>
                            <IconButton size="small" onClick={loadReceivers}><RefreshIcon fontSize="small" /></IconButton>
                        </Tooltip>
                    </Stack>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip size="small" color="success" label={t('hardcoded.components_settings_companynetworkstab.registrados_receiverssummar', { receiverssummar: receivers.summary?.registered || 0 })} />
                        <Chip size="small" color="warning" label={t('hardcoded.components_settings_companynetworkstab.pendientes_receiverssummar', { receiverssummar: receivers.summary?.pending || 0 })} />
                        <Chip size="small" label={`Sin datos: ${receivers.summary?.no_data || 0}`} />
                        <Chip size="small" color="error" label={t('hardcoded.components_settings_companynetworkstab.faltan_receiverssummar', { receiverssummar: receivers.summary?.missing || 0 })} />
                    </Stack>
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.5 }}>
                    <TextField
                        size="small"
                        placeholder={t('hardcoded.components_settings_companynetworkstab.buscar_por_mac_vehiculo_ssid')}
                        value={searchRx}
                        onChange={(e) => setSearchRx(e.target.value)}
                        sx={{ flexGrow: 1 }}
                    />
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>{i18n.t('gps.table.status')}</InputLabel>
                        <Select value={statusFilter} label={t('gps.table.status')} onChange={(e) => setStatusFilter(e.target.value)}>
                            <MenuItem value="all">{i18n.t('hardcoded.components_alarmprofiles_assignmentstable.todos')}</MenuItem>
                            <MenuItem value="registered">{i18n.t('hardcoded.components_settings_companynetworkstab.registrados')}</MenuItem>
                            <MenuItem value="pending">{t('hardcoded.components_settings_companynetworkstab.pendientes')}</MenuItem>
                            <MenuItem value="no_data">{t('hardcoded.components_alarms_alarmlist.sin_datos')}</MenuItem>
                            <MenuItem value="missing">{i18n.t('hardcoded.components_settings_companynetworkstab.faltan_registrar')}</MenuItem>
                        </Select>
                    </FormControl>
                    <Button startIcon={<AddIcon />} variant="contained" onClick={() => setRxDialog({ open: true, id: null, mac: '', vehicleId: '', networkId: '' })}>
                        {i18n.t('receivers.registerReceiver')}</Button>
                </Stack>

                {/* Un bloque colapsable por cada red */}
                {networks.map((net) => {
                    const list = receiversByNetwork.byId.get(net.id) || [];
                    return (
                        <Accordion key={net.id} disableGutters defaultExpanded={list.some((r) => r.status === 'missing')}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
                                    <WifiIcon fontSize="small" color={net.active ? 'primary' : 'disabled'} />
                                    <Typography variant="body2" fontWeight={700}>{net.ssid}</Typography>
                                    <Chip size="small" label={t('hardcoded.components_settings_companynetworkstab.length_receptorlistlength1es', { length: list.length, listlength1es: list.length === 1 ? '' : 'es' })} sx={{ height: 20 }} />
                                    {list.some((r) => r.status === 'missing') && (
                                        <Chip size="small" color="error" label={t('hardcoded.components_settings_companynetworkstab.falta_mac')} sx={{ height: 20 }} />
                                    )}
                                </Stack>
                            </AccordionSummary>
                            <AccordionDetails>
                                {renderReceiverTable(list)}
                                <Box sx={{ mt: 1, textAlign: 'right' }}>
                                    <Button size="small" startIcon={<AddIcon />} onClick={() => setRxDialog({ open: true, id: null, mac: '', vehicleId: '', networkId: net.id })}>
                                        {t('hardcoded.components_settings_companynetworkstab.registrar_receptor_en_esta_red')}</Button>
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    );
                })}

                {/* Receptores sin red asignada */}
                {receiversByNetwork.noNet.length > 0 && (
                    <Accordion disableGutters>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
                                <RouterIcon fontSize="small" color="disabled" />
                                <Typography variant="body2" fontWeight={700}>{t('hardcoded.components_settings_companynetworkstab.sin_red_asignada')}</Typography>
                                <Chip size="small" label={`${receiversByNetwork.noNet.length}`} sx={{ height: 20 }} />
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                            {renderReceiverTable(receiversByNetwork.noNet)}
                        </AccordionDetails>
                    </Accordion>
                )}

                {!networks.length && receiversByNetwork.noNet.length === 0 && (
                    <Alert severity="info">{t('hardcoded.components_settings_companynetworkstab.crea_una_red_wifi_para')}</Alert>
                )}
            </Paper>

            <NetworkDialog
                open={dialog.open}
                onClose={() => setDialog({ open: false, initial: null })}
                onSaved={load}
                users={users}
                initial={dialog.initial}
            />

            {/* Diálogo: registrar / editar receptor desde Redes */}
            <Dialog open={rxDialog.open} onClose={closeRxDialog} maxWidth="xs" fullWidth>
                <DialogTitle>{rxDialog.id ? t('receivers.actions.editReceiver') : t('receivers.registerReceiver')}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label={t('hardcoded.components_settings_companynetworkstab.mac_del_receptor')}
                            value={rxDialog.mac}
                            onChange={(e) => setRxDialog({ ...rxDialog, mac: e.target.value })}
                            placeholder="AA:BB:CC:DD:EE:FF"
                            fullWidth
                            autoFocus
                            helperText={t('hardcoded.components_settings_companynetworkstab.12_caracteres_hexadecimales_aceptamos_separadores')}
                        />
                        <FormControl size="small" fullWidth>
                            <InputLabel>{i18n.t('hardcoded.components_settings_companynetworkstab.red_wifi_opcional')}</InputLabel>
                            <Select
                                label={t('hardcoded.components_settings_companynetworkstab.red_wifi_opcional')}
                                value={rxDialog.networkId || ''}
                                onChange={(e) => setRxDialog({ ...rxDialog, networkId: e.target.value })}
                            >
                                <MenuItem value=""><em>{t('hardcoded.components_settings_companynetworkstab.sin_red')}</em></MenuItem>
                                {networks.map((n) => (
                                    <MenuItem key={n.id} value={n.id}>{n.ssid}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label={t('hardcoded.components_settings_companynetworkstab.vehiculo_opcional')}
                            value={rxDialog.vehicleId}
                            onChange={(e) => setRxDialog({ ...rxDialog, vehicleId: e.target.value })}
                            placeholder={t('hardcoded.components_settings_companynetworkstab.uuid_del_vehiculo')}
                            fullWidth
                            helperText={t('hardcoded.components_settings_companynetworkstab.puedes_asignar_el_vehiculo_luego')}
                        />
                        {!rxDialog.id && (
                            <Alert severity="info" icon={<EmailIcon />}>
                                {t('hardcoded.components_settings_companynetworkstab.al_registrar_se_notificara_por')}</Alert>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeRxDialog}>{t('common.cancel')}</Button>
                    <Button onClick={handleSaveReceiver} variant="contained" disabled={registering}>
                        {registering ? <CircularProgress size={18} /> : (rxDialog.id ? t('common.save') : t('gps.form.register'))}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CompanyNetworksTab;
